import { create } from "zustand";

export type DeliveryTime = "AM" | "EARLY_PM" | "FLEXIBLE";

export type AdditionalContact = {
  name: string;
  email: string;
  phone: string;
};

export type DeliveryContact = {
  name: string;
  email: string;
  phone: string;
  additionalContacts: AdditionalContact[];
};

export type BuyerDetails = {
  name: string;
  email: string;
  phone: string;
};

export type AccountSetupData = {
  password: string;
  confirmPassword: string;
  acceptedTerms: boolean;
  supplierAcknowledgement: boolean;
};

export type DeliveryPreferencesStepKey =
  | "deliveryTime"
  | "deliveryContact"
  | "buyerDetails"
  | "emailVerification"
  | "accountSetup"
  | "completion";

type DeliveryPreferencesState = {
  currentStep: DeliveryPreferencesStepKey;
  deliveryTime: DeliveryTime | null;
  deliveryContact: DeliveryContact | null;
  buyerDetails: BuyerDetails | null;
  password: string;
  confirmPassword: string;
  acceptedTerms: boolean;
  supplierAcknowledgement: boolean;
};

type DeliveryPreferencesActions = {
  setStep: (step: DeliveryPreferencesStepKey) => void;
  next: () => void;
  prev: () => void;
  setDeliveryTime: (value: DeliveryTime) => void;
  setDeliveryContact: (value: DeliveryContact) => void;
  setBuyerDetails: (value: BuyerDetails) => void;
  setAccountSetup: (value: AccountSetupData) => void;
  reset: () => void;
};

const STEP_ORDER: DeliveryPreferencesStepKey[] = [
  "deliveryTime",
  "deliveryContact",
  "buyerDetails",
  "emailVerification",
  "accountSetup",
  "completion",
];

const initialState: DeliveryPreferencesState = {
  currentStep: "deliveryTime",
  deliveryTime: null,
  deliveryContact: null,
  buyerDetails: null,
  password: "",
  confirmPassword: "",
  acceptedTerms: false,
  supplierAcknowledgement: false,
};

export const useDeliveryPreferencesStore = create<
  DeliveryPreferencesState & DeliveryPreferencesActions
>((set, get) => ({
  ...initialState,

  setStep: (step) => set({ currentStep: step }),

  next: () => {
    const idx = STEP_ORDER.indexOf(get().currentStep);
    if (idx < STEP_ORDER.length - 1) {
      set({ currentStep: STEP_ORDER[idx + 1] });
    }
  },

  prev: () => {
    const idx = STEP_ORDER.indexOf(get().currentStep);
    if (idx > 0) {
      set({ currentStep: STEP_ORDER[idx - 1] });
    }
  },

  setDeliveryTime: (value) => set({ deliveryTime: value }),
  setDeliveryContact: (value) => set({ deliveryContact: value }),
  setBuyerDetails: (value) => set({ buyerDetails: value }),
  setAccountSetup: (value) =>
    set({
      password: value.password,
      confirmPassword: value.confirmPassword,
      acceptedTerms: value.acceptedTerms,
      supplierAcknowledgement: value.supplierAcknowledgement,
    }),

  reset: () => set(initialState),
}));
